import React, { useState, useEffect, useContext, useRef } from 'react';
import { array, func, bool, string } from 'prop-types';
import classNames from 'classnames/bind';

// import Hint from 'components/Hint';

import { withErrorBoundary } from 'containers/ErrorBoundary';

import CommonLabel from 'components/CommonLabel';
import FieldNoResultsTooltip from 'components/FieldNoResultsTooltip';

import { StaticTextContext } from 'context/StaticTextContext';
import { SearchContext } from 'context/SearchContext';
import { CountryContext } from 'context/CountryContext';

import { getCarsByPlateNumber } from 'utils/getCarsByPlateNumber';
import { usePartDetailsLazyQuery } from 'utils/getPartAutosuggestions';
import { getCarDetails } from 'utils/getCarAutosuggestions';
import { isBlock } from 'utils/browserHistory';

import EuroIcon from 'media/icons/euro.icon.svg';
import CheckIcon from 'media/icons/checkmark.icon.svg';
import styles from './CarPlateSearch.module.scss';
const cx = classNames.bind(styles);

const CarPlateSearch = ({ setPartValue, setPartData, isRequired }) => {
  const [isError, setIsError] = useState(null);
  const { currentCountry } = useContext(CountryContext);
  const { labels } = useContext(StaticTextContext);
  const {
    setIsErrorPart,
    setIsErrorVinVehicle,
    setIsVinVehicleTooltip,
    setIsSelectedCar,
    setIsErrorCar,
    setIsErrorModel,
    setIsErrorManu,
    setIsSelectedManu,
    setIsSelectedModel,
    setIsSelectedCarPlate,
    setIsEditedManuField,
    setIsEditedModelField,
    setIsEditedCarField,

    setManuName,
    setManuId,
    setModelName,
    setModelId,
    setCarName,
    setCarId,
    setPartName,
    setPartId,
    setCarPlate,
    setCarTypeName,
    isNoCarPlateResults,
    setIsNoCarPlateResults,

    isSelectedCarPlate,
    carPlate,
    vehicleType,
    manuFieldRef,
  } = useContext(SearchContext);
  let vinRef = useRef();
  const getPartDetails = usePartDetailsLazyQuery();

  const setClearSearchState = () => {
    // Update context.
    setIsNoCarPlateResults(false);
    setIsError(false);

    setIsErrorCar(false);
    setIsErrorModel(false);
    setIsErrorManu(false);
    setIsSelectedManu(false);
    setIsSelectedModel(false);
    setIsSelectedCar(false);
    setIsEditedManuField(false);
    setIsEditedModelField(false);
    setIsEditedCarField(false);
    setIsSelectedCarPlate(false);

    setPartData([]);
    setPartValue('');
    setPartName('');
    setPartId(null);

    setCarTypeName('');
    setCarName('');
    setCarId(null);
    setModelName('');
    setModelId(null);
    setManuName('');
    setManuId(null);
    setPartName('');
    setPartId(null);
  };

  const handleOnBlur = (event) => {
    const currentValue = event.target.value;
    const trimmedValue = currentValue.replace(/-/g, '');

    setIsError(trimmedValue.length < 7 && currentValue.length > 0);
  };

  const handleOnFocus = () => {
    setIsError(false);
  };

  const partRequestSuccess = ({ status, data: response }) => {
    const partField = document.getElementById('search-part');

    if (status === 200 && response.status === 200) {
      const data = response.data.array ? response.data.array : [];
      setPartData(data);
      setIsErrorPart(false);

      partField && partField.focus();
    }
  };

  const handleToltipLinkClick = (e) => {
    e.preventDefault();
    setClearSearchState();
    setCarPlate('');
    manuFieldRef.current.input && manuFieldRef.current.input.focus();
  };

  const getCarDetailsSuccess = ({ status, data: response }) => {
    if (status === 200 && response.status === 200) {
      const vehicle =
        response.data && response.data.array && response.data.array[0]
          ? response.data.array[0].vehicleDetails
          : {};
      const {
        carId = null,
        modId = null,
        manuId = null,
        typeName,
        modelName,
        manuName,
      } = vehicle;

      if (carId) {
        setCarId(carId);
        setCarTypeName(typeName);
        setModelId(modId);
        setModelName(modelName);
        setManuId(manuId);
        setManuName(manuName);
        setIsSelectedCarPlate(true);

        getPartDetails(carId, currentCountry, vehicleType).then(
          partRequestSuccess
        );
      }
    }
  };

  const getCarsByPlateSuccess = ({ status, data: response }) => {
    const carId =
      response.liens_tecdoc && response.liens_tecdoc[0].lien_tecdoc[0].typnr;

    if (carId) {
      getCarDetails([carId], currentCountry, vehicleType)
        .then(getCarDetailsSuccess)
        .catch(() => {
          setIsError(true);
        });
    } else {
      setIsNoCarPlateResults(true);
    }
  };

  const handleOnChange = (event) => {
    const currentValue = event.target.value;
    const trimmedValue = currentValue.replace(/-/g, '');
    setClearSearchState();

    setCarPlate(currentValue);

    if (trimmedValue.length >= 7) {
      setIsErrorVinVehicle(false);
      setIsVinVehicleTooltip(false);

      getCarsByPlateNumber(currentValue)
        .then(getCarsByPlateSuccess)
        .catch(() => {
          setIsError(false);
          setIsNoCarPlateResults(false);
          setClearSearchState();
        });
    }
  };

  useEffect(() => {
    if (currentCountry !== '.com') {
      !isBlock && vinRef.current && vinRef.current.focus();
    }
  }, [currentCountry]);

  // Lenght of input value is equal to 17, because it is the standart of VIN number length.
  return (
    <div className={cx('root')}>
      <CommonLabel htmlFor="search-plate">
        {labels.searchCarPlateLabel}
      </CommonLabel>

      <div
        className={cx('form-control-wrap', {
          'is-filled': isSelectedCarPlate,
        })}
      >
        <div className={cx('icon-euro-wrap')}>
          <EuroIcon className={cx('icon-euro')} />
        </div>
        <input
          className={cx('form-control', {
            'is-error': isError && carPlate.length,
            'is-filled': isSelectedCarPlate,
          })}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          onChange={handleOnChange}
          type="text"
          id="search-plate"
          minLength="7"
          maxLength="15"
          ref={vinRef}
          disabled={currentCountry === '.com'}
          value={carPlate}
          placeholder={labels.searchCarPlatePlaceholder}
          required={isRequired}
          autoFocus={!isBlock && window.innerWidth > 768}
        />
        {isError && (
          <div className={cx('error-message', 'is-error')}>
            {labels.errorCarPlateField}
          </div>
        )}
        {isNoCarPlateResults && (
          <FieldNoResultsTooltip
            title={labels.carPlateTooltipTitle}
            description={labels.carPlateTooltipDescription}
            handleLinkClick={handleToltipLinkClick}
            className={cx('tooltip')}
          />
        )}
        <div className={cx('icon-check')}>
          <CheckIcon />
        </div>
      </div>
    </div>
  );
};

CarPlateSearch.propTypes = {
  pageType: string.isRequired,
  isRequired: bool.isRequired,
  partData: array,
  setPartData: func.isRequired,
  setPartValue: func.isRequired,
};

CarPlateSearch.defaultProps = {
  partData: [],
};

export default withErrorBoundary(CarPlateSearch);



// WEBPACK FOOTER //
// ./src/components/CarPlateSearch/CarPlateSearch.js